import React, {ComponentType, FunctionComponent, ReactElement, useState} from 'react';
import {sanitize} from 'dompurify';
import ReactDOM from 'react-dom';
import styles from './thickbox.pcss';

/**
 * Thickbox Modal handling.
 *
 * @version 3.0.0-go-live
 */

export interface ThickboxInteractions {
	changeTitle: () => void;
	close: () => void;
	isOpen?: boolean;
	onClose?: () => void;
}

export interface ThickboxProps {
	children: ReactElement;
	height?: number;
	isOpen?: boolean | null;
	onClose?: () => void;
	title?: string;
	width?: number;
}

/**
 * Open a modal.
 *
 * May be passed a static component or a lazy loaded one.
 *
 * @example
 * const HeavyModal = lazy( () => import( "../components/modal" ) );
 * openModal(() => (
  <Suspense fallback={<Loading />}>
  <HeavyModal />
  </Suspense>
  ),"Sharing is caring",{});
 *
 *
 * @param  WrappedComponent
 * @param  title
 * @param  props
 * @param  onClose
 * @param  height
 * @param  width
 */
export function openModal<P extends object>(
	WrappedComponent: ComponentType<P>,
	title,
	props: P,
	onClose: () => void = () => {
	},
	height: number = 540,
	width: number = 600
) {
	const holder = document.getElementById( 'thickbox-place-holder' );
	if ( ! holder ) {
		const div = document.createElement( 'div' );
		div.id = 'thickbox-place-holder';
		document.body.appendChild( div );
	}

	ReactDOM.render(
		<Thickbox
			width={width}
			height={height}
			title={title}
			key={Date.now() + 'thickbox'}
			onClose={onClose}
		>
			<WrappedComponent {...props} />
		</Thickbox>,
		document.getElementById( 'thickbox-place-holder' )
	);
}

/**
 * Higher order component use of the Thickbox to wrap
 * a component directly instead of within the tree.
 *
 * @param  WrappedComponent
 * @param  thickboxProps
 */
export function withModal<P extends ThickboxInteractions>(
	WrappedComponent: ComponentType<P>,
	thickboxProps: Omit<ThickboxProps, 'children'>
): FunctionComponent<Omit<P, 'close' | 'changeTitle'>> {
	return ( props: P ) => {
		return (
			<Thickbox {...props} {...thickboxProps}>
				<WrappedComponent {...props} />
			</Thickbox>
		);
	};
}

const Thickbox = ( {
	width = 600,
	height = 540,
	title = '',
	onClose = () => null,
	isOpen = null,
	children,
}: ThickboxProps ) => {
	const [ open, setOpen ] = useState( true );
	const [ titleHtml, changeTitle ] = useState( title );

	const onCloseButtonClick = () => {
		onClose();
		setOpen( false );
	};

	const left = width / 2;
	const top = height / 2;

	const windowStyles = {
		width: width + 'px',
		height: height + 'px',
		margin: '-' + top + 'px 0 0 -' + left + 'px',
	};

	// Handled by state.
	if ( null === isOpen ) {
		if ( ! open ) {
			return null;
		}
		// Handed by an external prop.
	} else if ( ! isOpen ) {
		return null;
	}

	return (
		<div>
			<div className={styles.overlay} />
			<div className={styles.window} style={windowStyles}>
				{'' !== titleHtml && (
					<div className={styles.title}>
						{/** phpcs:disable */}
						<div
							className={styles.windowTitle}
							dangerouslySetInnerHTML={{
								__html: sanitize( titleHtml ),
							}}
						/>
						<div className={styles.close}>
							<span
								className={styles.closeIcon +
								' dashicons dashicons-no-alt'}
								onClick={onCloseButtonClick}
								onKeyPress={onCloseButtonClick}
								role="button"
								tabIndex={0}
							/>
						</div>
					</div>
				)}

				<div className={styles.content}>
					{/* Add the close method as a prop to each child, so we can close inner components */}
					{React.cloneElement( children, {
						close: onCloseButtonClick,
						changeTitle,
					} )}
				</div>
			</div>
		</div>
	);
};

export default Thickbox;
